import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, CheckCircle, AlertCircle, Loader2, ThumbsUp, ThumbsDown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ value, onChange, label, description }) => {
  const [hover, setHover] = useState(0);
  const labels = ['Poor', 'Below Average', 'Average', 'Good', 'Excellent'];
  
  return (
    <div className="space-y-2">
      <div>
        <label className="text-sm font-medium text-foreground">{label}</label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="focus:outline-none transition-transform hover:scale-110"
              data-testid={`${label.toLowerCase().replace(/\s/g, '-')}-star-${star}`}
            >
              <Star 
                className={`w-7 h-7 ${
                  star <= (hover || value) 
                    ? 'fill-amber-400 text-amber-400' 
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>
        {(hover || value) > 0 && (
          <span className="text-sm text-muted-foreground ml-2">
            {labels[(hover || value) - 1]}
          </span>
        )}
      </div>
    </div>
  );
};

const YesNoQuestion = ({ label, description, value, onChange, name }) => (
  <div className="space-y-2">
    <div>
      <label className="text-sm font-medium text-foreground">{label}</label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-4">
      <div className="flex items-center space-x-2">
        <RadioGroupItem value="yes" id={`${name}-yes`} />
        <Label htmlFor={`${name}-yes`} className="flex items-center gap-1 cursor-pointer">
          <ThumbsUp className="w-4 h-4 text-green-600" /> Yes
        </Label>
      </div>
      <div className="flex items-center space-x-2">
        <RadioGroupItem value="no" id={`${name}-no`} />
        <Label htmlFor={`${name}-no`} className="flex items-center gap-1 cursor-pointer">
          <ThumbsDown className="w-4 h-4 text-red-600" /> No
        </Label>
      </div>
    </RadioGroup>
  </div>
);

const FeedbackForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  // Comprehensive feedback state
  const [feedback, setFeedback] = useState({
    // Service Quality
    overall_experience: 0,
    value_for_money: 0,
    would_recommend: '',
    would_visit_again: '',
    
    // Therapist Ratings
    therapist_skill: 0,
    therapist_professionalism: 0,
    therapist_communication: 0,
    therapist_punctuality: 0,
    pressure_comfort: '',
    
    // Ambiance & Cleanliness
    room_cleanliness: 0,
    room_ambiance: 0,
    hygiene_standards: 0,
    towels_linens_quality: 0,
    
    // Booking & Service
    booking_ease: 0,
    wait_time_satisfaction: 0,
    service_duration_satisfaction: '',
    staff_courtesy: 0,
    
    // What stood out
    what_you_liked: '',
    areas_of_improvement: '',
    additional_comments: ''
  });

  useEffect(() => {
    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/feedback/validate/${token}`);
      setTokenInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired feedback link');
    } finally {
      setLoading(false);
    }
  };

  const updateFeedback = (key, value) => {
    setFeedback(prev => ({ ...prev, [key]: value }));
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      return feedback.overall_experience > 0 && feedback.value_for_money > 0 && 
             feedback.would_recommend && feedback.would_visit_again;
    }
    if (stepNum === 2) {
      return feedback.therapist_skill > 0 && feedback.therapist_professionalism > 0 &&
             feedback.therapist_communication > 0 && feedback.pressure_comfort;
    }
    if (stepNum === 3) {
      return feedback.room_cleanliness > 0 && feedback.room_ambiance > 0 &&
             feedback.hygiene_standards > 0;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep(step)) {
      toast.error('Please complete all required fields');
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(step)) {
      toast.error('Please complete all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/feedback/submit`, {
        token,
        feedback_type: 'customer_to_therapist',
        ...feedback
      });
      
      setSubmitted(true);
      toast.success('Thank you for your detailed feedback!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Validating feedback link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream flex items-center justify-center p-4">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-serif text-foreground mb-2">Link Invalid</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream flex items-center justify-center p-4">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-2xl font-serif text-foreground mb-2">Thank You!</h1>
          <p className="text-muted-foreground mb-4">
            Your detailed feedback has been submitted successfully. Your insights help us improve our services and recognize our therapists' excellent work.
          </p>
          <div className="flex items-center justify-center gap-2 text-primary">
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">WellNest Technologies</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-serif text-foreground">Share Your Experience</h1>
          <p className="text-muted-foreground mt-1">Your feedback helps us serve you better</p>
        </div>

        {/* Service Info Card */}
        <div className="glass rounded-xl p-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Guest</p>
              <p className="font-medium">{tokenInfo?.guest_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Therapist</p>
              <p className="font-medium">{tokenInfo?.therapist_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Service</p>
              <p className="font-medium">{tokenInfo?.service_type || tokenInfo?.therapy_type}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Date</p>
              <p className="font-medium">{tokenInfo?.service_date || tokenInfo?.date}</p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s === step ? 'bg-primary text-white' : 
                s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s < step ? '✓' : s}
              </div>
              {s < 4 && <div className={`w-8 h-0.5 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-muted-foreground mb-4">
          Step {step} of 4: {
            step === 1 ? 'Overall Experience' :
            step === 2 ? 'Therapist Feedback' :
            step === 3 ? 'Ambiance & Cleanliness' :
            'Final Thoughts'
          }
        </p>

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} className="glass rounded-2xl p-6">
          {/* Step 1: Overall Experience */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-serif text-foreground border-b pb-2">Overall Experience</h2>
              
              <StarRating
                label="Overall Experience *"
                description="How would you rate your overall spa experience?"
                value={feedback.overall_experience}
                onChange={(v) => updateFeedback('overall_experience', v)}
              />
              
              <StarRating
                label="Value for Money *"
                description="Did you feel the service was worth the price?"
                value={feedback.value_for_money}
                onChange={(v) => updateFeedback('value_for_money', v)}
              />
              
              <YesNoQuestion
                label="Would you recommend us to friends & family? *"
                value={feedback.would_recommend}
                onChange={(v) => updateFeedback('would_recommend', v)}
                name="recommend"
              />
              
              <YesNoQuestion
                label="Would you visit us again? *"
                value={feedback.would_visit_again}
                onChange={(v) => updateFeedback('would_visit_again', v)}
                name="visit-again"
              />
            </div>
          )}

          {/* Step 2: Therapist Feedback */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-serif text-foreground border-b pb-2">
                Therapist Feedback: {tokenInfo?.therapist_name}
              </h2>
              
              <StarRating
                label="Skill & Technique *"
                description="How skilled was the therapist in performing the treatment?"
                value={feedback.therapist_skill}
                onChange={(v) => updateFeedback('therapist_skill', v)}
              />
              
              <StarRating
                label="Professionalism *"
                description="Did the therapist maintain professional conduct?"
                value={feedback.therapist_professionalism}
                onChange={(v) => updateFeedback('therapist_professionalism', v)}
              />
              
              <StarRating
                label="Communication *"
                description="Did the therapist communicate clearly about the treatment?"
                value={feedback.therapist_communication}
                onChange={(v) => updateFeedback('therapist_communication', v)}
              />
              
              <StarRating
                label="Punctuality"
                description="Did the session start on time?"
                value={feedback.therapist_punctuality}
                onChange={(v) => updateFeedback('therapist_punctuality', v)}
              />
              
              <YesNoQuestion
                label="Was the pressure/intensity comfortable? *"
                description="Did the therapist adjust to your comfort level?"
                value={feedback.pressure_comfort}
                onChange={(v) => updateFeedback('pressure_comfort', v)}
                name="pressure"
              />
            </div>
          )}

          {/* Step 3: Ambiance & Cleanliness */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-serif text-foreground border-b pb-2">Ambiance & Cleanliness</h2>
              
              <StarRating
                label="Room Cleanliness *"
                description="How clean was the treatment room?"
                value={feedback.room_cleanliness}
                onChange={(v) => updateFeedback('room_cleanliness', v)}
              />
              
              <StarRating
                label="Room Ambiance *"
                description="Lighting, music, temperature, aroma"
                value={feedback.room_ambiance}
                onChange={(v) => updateFeedback('room_ambiance', v)}
              />
              
              <StarRating
                label="Hygiene Standards *"
                description="Sanitization, disposables, overall hygiene"
                value={feedback.hygiene_standards}
                onChange={(v) => updateFeedback('hygiene_standards', v)}
              />
              
              <StarRating
                label="Towels & Linens Quality"
                description="Quality and freshness of towels/linens used"
                value={feedback.towels_linens_quality}
                onChange={(v) => updateFeedback('towels_linens_quality', v)}
              />
              
              <StarRating
                label="Booking Experience"
                description="How easy was it to book your appointment?"
                value={feedback.booking_ease}
                onChange={(v) => updateFeedback('booking_ease', v)}
              />
              
              <StarRating
                label="Staff Courtesy"
                description="Behavior of reception and other staff"
                value={feedback.staff_courtesy}
                onChange={(v) => updateFeedback('staff_courtesy', v)}
              />
            </div>
          )}

          {/* Step 4: Final Thoughts */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-serif text-foreground border-b pb-2">Final Thoughts</h2>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">What did you like the most?</Label>
                <Textarea
                  value={feedback.what_you_liked}
                  onChange={(e) => updateFeedback('what_you_liked', e.target.value)}
                  placeholder="Tell us what made your experience special..."
                  className="min-h-[80px]"
                  data-testid="what-liked"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Areas of Improvement</Label>
                <Textarea
                  value={feedback.areas_of_improvement}
                  onChange={(e) => updateFeedback('areas_of_improvement', e.target.value)}
                  placeholder="What could we do better? Your honest feedback helps us improve..."
                  className="min-h-[80px]"
                  data-testid="improvement"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Additional Comments</Label>
                <Textarea
                  value={feedback.additional_comments}
                  onChange={(e) => updateFeedback('additional_comments', e.target.value)}
                  placeholder="Any other thoughts you'd like to share..."
                  className="min-h-[80px]"
                  data-testid="additional-comments"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-4 border-t">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={() => setStep(step - 1)}>
                Previous
              </Button>
            ) : <div />}
            
            {step < 4 ? (
              <Button type="button" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button type="submit" disabled={submitting} className="min-w-[120px]">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </Button>
            )}
          </div>
        </form>
        
        <p className="text-center text-xs text-muted-foreground mt-6">
          Your feedback is confidential and helps us improve • WellNest Technologies
        </p>
      </div>
    </div>
  );
};

export default FeedbackForm;
